// useManuscriptGoals.js
// The manuscript's goals, loaded once and shared by the left panel, the goals
// view and the right rail. Every write answers with the goals as this user may
// see them (the server filters), so each action just replaces the list.
import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../../utils/api';
import { flattenGoals } from './goalModel';

const EMPTY = { goals: [], team: [], isAuthor: false, writing: null, loaded: false };

export default function useManuscriptGoals(draftId) {
  const [state, setState] = useState(EMPTY);
  const [error, setError] = useState(null);
  const base = `/api/drafts/${draftId}/goals`;

  const call = useCallback(async (method, path, body) => {
    const token = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(json?.error || `Request failed (${res.status})`);
    return json;
  }, []);

  // Days are the reader's own: the server counts "today" in this timezone.
  const reload = useCallback(async () => {
    if (!draftId) return;
    try {
      const json = await call('GET', `${base}?tzOffset=${new Date().getTimezoneOffset()}`);
      setState({
        goals: flattenGoals(json.milestones),
        team: json.assignees || [],
        isAuthor: !!json.is_author,
        writing: json.writing || null,
        loaded: true,
      });
      setError(null);
    } catch (e) {
      setError(e.message);
      setState((s) => ({ ...s, loaded: true }));
    }
  }, [draftId, base, call]);

  useEffect(() => { setState(EMPTY); reload(); }, [reload]);

  /** Run a write; on success replace the goals, on failure keep them and say why. */
  const act = useCallback(async (method, path, body) => {
    try {
      const json = await call(method, path, body);
      if (json?.milestones) setState((s) => ({ ...s, goals: flattenGoals(json.milestones) }));
      setError(null);
      return json || {};
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [call]);

  const task = (id) => `${base}/tasks/${id}`;
  return {
    ...state,
    error,
    clearError: () => setError(null),
    reload,
    create: (body) => act('POST', `${base}/milestones/0/tasks`, body),
    update: (id, patch) => act('PUT', task(id), patch),
    remove: (id) => act('DELETE', task(id)),
    cancel: (id) => act('PUT', `${task(id)}/cancel`),
    setComplete: (id, completed) => act('PUT', `${task(id)}/complete`, { completed }),
    logProgress: (id, delta) => act('PUT', `${task(id)}/progress`, { delta }),
    comment: (id, text) => act('POST', `${task(id)}/comments`, { text }),
    addItem: (id, text) => act('POST', `${task(id)}/checklist`, { text }),
    toggleItem: (id, itemId, done) => act('PUT', `${task(id)}/checklist/${itemId}`, { done }),
    removeItem: (id, itemId) => act('DELETE', `${task(id)}/checklist/${itemId}`),
    addNote: (id, text) => act('POST', `${task(id)}/notes`, { text }),
    addLink: (id, title, url) => act('POST', `${task(id)}/links`, { title, url }),
    removeLink: (id, linkId) => act('DELETE', `${task(id)}/links/${linkId}`),
  };
}
